import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

// Вкладка «Вход и регистрация» (149-ФЗ, закон от 09.06.2026):
//  - способы входа: вкл/выкл + ключи каждого провайдера (oauth_settings);
//  - политика регистрации: блок-лист иностранных email-доменов/зон,
//    исключения, Telegram-тумблеры (auth_registration_policy).
// Backend: admin-go /api/admin/auth-settings (НЕ legacy Node /admin/settings/oauth).

interface ProviderRow {
  provider: string;
  enabled: boolean;
  foreign: boolean;
  clientId: string;
  hasSecret: boolean;
  callbackUrl: string;
  hasBotToken: boolean;
  botUsername: string;
  webhookUrl: string;
  webappUrl: string;
}

interface Policy {
  blockForeignEmail: boolean;
  blockedDomains: string[];
  blockedZones: string[];
  allowedDomains: string[];
  telegramRegistrationEnabled: boolean;
  telegramLoginEnabled: boolean;
}

const PROVIDER_LABELS: Record<string, { label: string; hint?: string }> = {
  hh: { label: 'hh.ru', hint: 'Вход по телефону/почте с кодом через hh-bridge. Ключи не требуются — только тумблер.' },
  yandex: { label: 'Яндекс ID' },
  vkid: { label: 'VK ID', hint: 'OAuth 2.1 + PKCE (id.vk.com). Покрывает VK и Почту Mail.ru.' },
  mailru: { label: 'Mail.ru', hint: 'oauth.mail.ru, классический OAuth2.' },
  sberid: { label: 'Сбер ID', hint: 'Требуется партнёрский onboarding Сбера (client_id/secret).' },
  tid: { label: 'T-ID (Т-Банк)', hint: 'id.tbank.ru. Ключи выдаёт Т-Банк.' },
  esia: { label: 'Госуслуги (ЕСИА)', hint: 'Требуется регистрация ИС в ЕСИА и ГОСТ-сертификат. До завершения интеграции вход вернёт ошибку.' },
  telegram: { label: 'Telegram', hint: 'Иностранный сервис: регистрация новых пользователей управляется политикой ниже.' },
  google: { label: 'Google' },
  linkedin: { label: 'LinkedIn' },
};

const PROVIDER_ORDER = ['hh', 'yandex', 'vkid', 'mailru', 'sberid', 'tid', 'esia', 'telegram', 'google', 'linkedin'];

const AuthSettingsTab: React.FC = () => {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Локальные правки ключей (не отправляем на каждый keypress)
  const [drafts, setDrafts] = useState<Record<string, { clientId?: string; clientSecret?: string; callbackUrl?: string }>>({});
  const [domainsText, setDomainsText] = useState('');
  const [zonesText, setZonesText] = useState('');
  const [allowedText, setAllowedText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/auth-settings');
      if (res.data?.success) {
        setProviders(res.data.providers || []);
        setPolicy(res.data.policy);
        setDomainsText((res.data.policy?.blockedDomains || []).join('\n'));
        setZonesText((res.data.policy?.blockedZones || []).join('\n'));
        setAllowedText((res.data.policy?.allowedDomains || []).join('\n'));
      } else {
        setError(res.data?.message || 'Не удалось загрузить настройки');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не удалось загрузить настройки (применена ли миграция 119?)');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(null), 3000);
  };

  const saveProvider = async (provider: string, patch: any) => {
    setSavingProvider(provider);
    try {
      const res = await api.put(`/admin/auth-settings/providers/${provider}`, patch);
      if (!res.data?.success) throw new Error(res.data?.message);
      flash(`Сохранено: ${PROVIDER_LABELS[provider]?.label || provider}`);
      setDrafts((d) => ({ ...d, [provider]: {} }));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    } finally {
      setSavingProvider(null);
    }
  };

  const parseList = (text: string): string[] =>
    text.split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);

  const savePolicy = async (patch: any) => {
    setSavingPolicy(true);
    try {
      const res = await api.put('/admin/auth-settings/policy', patch);
      if (!res.data?.success) throw new Error(res.data?.message);
      flash('Политика сохранена');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сохранения политики');
    } finally {
      setSavingPolicy(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg shadow-md p-6 text-gray-500">Загрузка настроек входа…</div>;
  }

  const ordered = [...providers].sort(
    (a, b) => PROVIDER_ORDER.indexOf(a.provider) - PROVIDER_ORDER.indexOf(b.provider)
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex justify-between">
          <span>{error}</span>
          <button className="underline" onClick={() => { setError(null); load(); }}>Обновить</button>
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{notice}</div>
      )}

      {/* ===== Провайдеры входа ===== */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900">Способы входа</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          По 149-ФЗ (закон от 09.06.2026) авторизация возможна только через российские сервисы:
          российская почта, Госуслуги, Сбер ID, T-ID, VK ID, Яндекс и др. Иностранные провайдеры заблокированы.
        </p>
        <div className="space-y-4">
          {ordered.map((p) => {
            const meta = PROVIDER_LABELS[p.provider] || { label: p.provider };
            const draft = drafts[p.provider] || {};
            const isTg = p.provider === 'telegram';
            return (
              <div key={p.provider} className={`border rounded-lg p-4 ${p.foreign ? 'bg-gray-50 border-gray-200' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900">{meta.label}</span>
                    {p.foreign && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">запрещён 149-ФЗ</span>
                    )}
                    {p.enabled && !p.foreign && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">включён</span>
                    )}
                  </div>
                  {!p.foreign && (
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={p.enabled}
                        disabled={savingProvider === p.provider}
                        onChange={(e) => saveProvider(p.provider, { enabled: e.target.checked })}
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 rounded-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                    </label>
                  )}
                </div>
                {meta.hint && <p className="text-xs text-gray-500 mt-1">{meta.hint}</p>}

                {!p.foreign && !isTg && p.provider !== 'hh' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                    <input
                      type="text"
                      placeholder="Client ID"
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={draft.clientId ?? p.clientId}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.provider]: { ...d[p.provider], clientId: e.target.value } }))}
                    />
                    <input
                      type="password"
                      placeholder={p.hasSecret ? 'Client Secret (сохранён — введите для замены)' : 'Client Secret'}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={draft.clientSecret ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.provider]: { ...d[p.provider], clientSecret: e.target.value } }))}
                    />
                    <input
                      type="text"
                      placeholder={`Callback URL (https://rabotamax.ru/auth/${p.provider}/callback)`}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={draft.callbackUrl ?? p.callbackUrl}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.provider]: { ...d[p.provider], callbackUrl: e.target.value } }))}
                    />
                    {(draft.clientId !== undefined || draft.clientSecret || draft.callbackUrl !== undefined) && (
                      <button
                        onClick={() => {
                          const patch: any = {};
                          if (draft.clientId !== undefined) patch.clientId = draft.clientId;
                          if (draft.clientSecret) patch.clientSecret = draft.clientSecret;
                          if (draft.callbackUrl !== undefined) patch.callbackUrl = draft.callbackUrl;
                          saveProvider(p.provider, patch);
                        }}
                        disabled={savingProvider === p.provider}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-4 py-2 md:col-span-3 md:w-44 disabled:opacity-50"
                      >
                        {savingProvider === p.provider ? 'Сохраняем…' : 'Сохранить ключи'}
                      </button>
                    )}
                  </div>
                )}
                {isTg && (
                  <p className="text-xs text-gray-500 mt-2">
                    Токен и параметры бота управляются на вкладке «OAuth и интеграции» (legacy).
                    Здесь — только включение способа входа; регистрация новых через Telegram — в политике ниже.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Политика регистрации ===== */}
      {policy && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900">Политика регистрации (149-ФЗ)</h2>

          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={policy.blockForeignEmail}
                disabled={savingPolicy}
                onChange={(e) => savePolicy({ blockForeignEmail: e.target.checked })}
              />
              <span className="text-sm text-gray-800">Блокировать регистрацию с иностранными email-доменами</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={policy.telegramRegistrationEnabled}
                disabled={savingPolicy}
                onChange={(e) => savePolicy({ telegramRegistrationEnabled: e.target.checked })}
              />
              <span className="text-sm text-gray-800">
                Разрешить регистрацию НОВЫХ пользователей через Telegram
                <span className="text-xs text-red-600 ml-1">(включение нарушает 149-ФЗ)</span>
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={policy.telegramLoginEnabled}
                disabled={savingPolicy}
                onChange={(e) => savePolicy({ telegramLoginEnabled: e.target.checked })}
              />
              <span className="text-sm text-gray-800">Разрешить вход СУЩЕСТВУЮЩИМ пользователям через Telegram</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Заблокированные домены <span className="text-gray-400">({policy.blockedDomains.length})</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono h-48"
                value={domainsText}
                onChange={(e) => setDomainsText(e.target.value)}
                placeholder={'gmail.com\nyahoo.com\n…'}
              />
              <p className="text-xs text-gray-400 mt-1">По одному на строку. Поддомены блокируются автоматически.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Заблокированные зоны <span className="text-gray-400">({policy.blockedZones.length})</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono h-48"
                value={zonesText}
                onChange={(e) => setZonesText(e.target.value)}
                placeholder={'целые зоны, напр.:\nco.uk'}
              />
              <p className="text-xs text-gray-400 mt-1">Блокирует все домены в зоне.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Исключения (разрешены всегда) <span className="text-gray-400">({policy.allowedDomains.length})</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono h-48"
                value={allowedText}
                onChange={(e) => setAllowedText(e.target.value)}
                placeholder={'corp-domain.com'}
              />
              <p className="text-xs text-gray-400 mt-1">Приоритетнее блок-листа.</p>
            </div>
          </div>

          <button
            onClick={() => savePolicy({
              blockedDomains: parseList(domainsText),
              blockedZones: parseList(zonesText),
              allowedDomains: parseList(allowedText),
            })}
            disabled={savingPolicy}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-5 py-2.5 disabled:opacity-50"
          >
            {savingPolicy ? 'Сохраняем…' : 'Сохранить списки доменов'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthSettingsTab;
